// Video slice — frame rate, frame counter, image size, seek-in-flight gate.
// Spec §4.2 (state inventory), §7.1 (rVFC seek protocol that flips
// `seekInFlight`).
//
// Pick-only navigation: when `navigableFrames` is set (image loader),
// `setCurrentFrame` snaps to the nearest entry. This is the single
// chokepoint that all paths — slider drags, arrow keys, programmatic
// jumps — flow through, so no caller needs format-specific branching.

import type { StateCreator } from 'zustand';
import type { AppState } from './index';
import type { Side } from './predictions';

export interface VideoMeta {
  fps: number;
  totalFrames: number;
  imageSize: [number, number];
}

export interface VideoSlice {
  fps: number;
  totalFrames: number;
  currentFrame: number;
  imageSize: [number, number];
  /**
   * Per-side native image dimensions, populated from each `<video>`/`<img>`'s
   * own `videoWidth/Height` (or `naturalWidth/Height`) on load. Lets the 4
   * views self-adjust to their own aspect ratio when cameras differ
   * (e.g. front 2592×1944 vs side 1920×1080). The global `imageSize` above
   * stays as the front_left-derived value used by the save path.
   */
  imageSizes: Partial<Record<Side, [number, number]>>;
  seekInFlight: boolean;
  /**
   * Sorted, deduped list of frame indices the user is allowed to navigate
   * to. `null` means "all frames [0, totalFrames)" — set by the video loader.
   * Set to the pick list by the image loader.
   */
  navigableFrames: number[] | null;
  /**
   * Sorted, deduped frame indices the annotator has confirmed ("audited") by
   * clicking Next. Drives the Submit gate (all picked frames must be audited)
   * and is persisted in the draft so progress survives reload / Pending.
   */
  auditedFrames: number[];
  /**
   * Time (ms) banked from previous annotation sessions of this episode,
   * restored from the draft. The currently-running session's time is added on
   * top via `timerStartedAt` — see `episodeElapsedMs`.
   */
  timerBaseMs: number;
  /**
   * `Date.now()` captured when the current annotate session's clock started,
   * or `null` when the timer is not running. Live elapsed time is
   * `timerBaseMs + (now - timerStartedAt)`.
   */
  timerStartedAt: number | null;
  setMeta: (meta: VideoMeta) => void;
  setSideImageSize: (side: Side, size: [number, number]) => void;
  setCurrentFrame: (frame: number) => void;
  /**
   * Write `currentFrame` directly, bypassing the `navigableFrames` snap.
   * Intended ONLY for the progress-bar Ctrl-drag preview path — every other
   * caller must use `setCurrentFrame` to preserve the pick-only invariant.
   */
  setCurrentFrameUnsnapped: (frame: number) => void;
  setSeekInFlight: (inFlight: boolean) => void;
  setNavigableFrames: (frames: number[] | null) => void;
  /** Mark a frame audited (idempotent; keeps the list sorted). */
  markAudited: (frame: number) => void;
  /** Replace the audited set wholesale (used when restoring a draft). */
  setAuditedFrames: (frames: number[]) => void;
  /** Start (or restart) the per-episode clock for the current session. */
  startEpisodeTimer: () => void;
  /** Reset the per-episode clock to zero and stop it. */
  resetEpisodeTimer: () => void;
}

/** Cumulative time (ms) spent on the current episode: time banked from prior
 *  sessions plus the in-flight session's running time. */
export function episodeElapsedMs(s: Pick<VideoSlice, 'timerBaseMs' | 'timerStartedAt'>): number {
  return s.timerBaseMs + (s.timerStartedAt !== null ? Date.now() - s.timerStartedAt : 0);
}

/** The frames that must be audited before Submit is allowed: the pick list
 *  when picks are active, otherwise every frame in the episode. */
function auditableFrames(navigableFrames: number[] | null, totalFrames: number): number[] {
  if (navigableFrames && navigableFrames.length > 0) return navigableFrames;
  return Array.from({ length: totalFrames }, (_, i) => i);
}

/** True when every auditable frame has been audited (and there is at least one). */
export function allAudited(
  navigableFrames: number[] | null,
  totalFrames: number,
  audited: number[]
): boolean {
  const frames = auditableFrames(navigableFrames, totalFrames);
  if (frames.length === 0) return false;
  const set = new Set(audited);
  return frames.every((f) => set.has(f));
}

/** The first auditable frame not yet audited, or `null` when all are done. */
export function firstUnaudited(
  navigableFrames: number[] | null,
  totalFrames: number,
  audited: number[]
): number | null {
  const set = new Set(audited);
  for (const f of auditableFrames(navigableFrames, totalFrames)) {
    if (!set.has(f)) return f;
  }
  return null;
}

/** Frame to land on after saving (green-ing) `current`.
 *
 *  Mirrors the three-state save workflow: grey = untouched (neither saved nor
 *  edited), orange = touched-but-unsaved (edited, not saved), green = saved.
 *  Scans forward from just after `current` for the next still-unsaved frame
 *  (grey or orange). If none lies ahead, it wraps over the whole list,
 *  preferring the first untouched (grey) frame, then the first
 *  touched-but-unsaved (orange) frame. Returns `null` when every frame is
 *  saved (→ the caller opens the completion dialog). */
export function nextSaveTarget(
  navigableFrames: number[] | null,
  totalFrames: number,
  saved: number[],
  edited: number[],
  current: number
): number | null {
  const frames = auditableFrames(navigableFrames, totalFrames);
  const savedSet = new Set(saved);
  const editedSet = new Set(edited);

  // 1. Next unsaved frame ahead of the current one (in list order).
  const idx = frames.indexOf(current);
  if (idx >= 0) {
    for (let i = idx + 1; i < frames.length; i++) {
      if (!savedSet.has(frames[i]!)) return frames[i]!;
    }
  }
  // 2. Wrap: first untouched (grey) frame anywhere in the list.
  for (const f of frames) {
    if (!savedSet.has(f) && !editedSet.has(f)) return f;
  }
  // 3. Wrap: first touched-but-unsaved (orange) frame.
  for (const f of frames) {
    if (!savedSet.has(f)) return f;
  }
  // 4. Everything is saved.
  return null;
}

/** Closest entry in a sorted, non-empty array to `n` (binary search). */
function snapToNearest(sorted: number[], n: number): number {
  // Callers guard sorted.length > 0.
  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  if (n <= first) return first;
  if (n >= last) return last;
  let lo = 0;
  let hi = sorted.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid]! < n) lo = mid + 1;
    else hi = mid;
  }
  // lo is the first entry >= n; its neighbour at lo-1 is the largest entry < n.
  const a = sorted[lo - 1]!;
  const b = sorted[lo]!;
  return n - a <= b - n ? a : b;
}

/** Step within `sorted` relative to `current`. delta=+1 → next pick; -1 → prev. */
export function stepNavigable(sorted: number[], current: number, delta: number): number {
  if (sorted.length === 0) return current;
  // Find the index of `current` (or where it would slot in).
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid]! < current) lo = mid + 1;
    else hi = mid;
  }
  const idx = lo; // smallest index with sorted[idx] >= current
  if (idx >= sorted.length || sorted[idx] !== current) {
    if (delta > 0) return sorted[Math.min(sorted.length - 1, idx)]!;
    return sorted[Math.max(0, idx - 1)]!;
  }
  const next = idx + delta;
  if (next < 0) return sorted[0]!;
  if (next >= sorted.length) return sorted[sorted.length - 1]!;
  return sorted[next]!;
}

export const createVideoSlice: StateCreator<
  AppState,
  [['zustand/immer', never]],
  [],
  VideoSlice
> = (set, get) => ({
  fps: 0,
  totalFrames: 0,
  currentFrame: 0,
  imageSize: [0, 0],
  imageSizes: {},
  seekInFlight: false,
  navigableFrames: null,
  auditedFrames: [],
  timerBaseMs: 0,
  timerStartedAt: null,
  setMeta: (meta) =>
    set((s) => {
      s.fps = meta.fps;
      s.totalFrames = meta.totalFrames;
      s.imageSize = meta.imageSize;
    }),
  setSideImageSize: (side, size) =>
    set((s) => {
      s.imageSizes[side] = size;
    }),
  setCurrentFrame: (frame) => {
    const navigable = get().navigableFrames;
    const target = navigable && navigable.length > 0 ? snapToNearest(navigable, frame) : frame;
    set((s) => {
      s.currentFrame = target;
    });
  },
  setCurrentFrameUnsnapped: (frame) =>
    set((s) => {
      s.currentFrame = frame;
    }),
  setSeekInFlight: (inFlight) =>
    set((s) => {
      s.seekInFlight = inFlight;
    }),
  setNavigableFrames: (frames) =>
    set((s) => {
      s.navigableFrames = frames;
      // Maintain the invariant that currentFrame is always inside the pick
      // list when picks are active. On first load this lifts the initial
      // `0` to the first pick (so ImageCanvas finds a JPG to render and the
      // setMeta cascade fires).
      if (frames && frames.length > 0 && !frames.includes(s.currentFrame)) {
        s.currentFrame = snapToNearest(frames, s.currentFrame);
      }
    }),
  markAudited: (frame) =>
    set((s) => {
      if (!s.auditedFrames.includes(frame)) {
        s.auditedFrames.push(frame);
        s.auditedFrames.sort((a, b) => a - b);
      }
    }),
  setAuditedFrames: (frames) =>
    set((s) => {
      s.auditedFrames = [...frames].sort((a, b) => a - b);
    }),
  startEpisodeTimer: () =>
    set((s) => {
      s.timerStartedAt = Date.now();
    }),
  resetEpisodeTimer: () =>
    set((s) => {
      s.timerBaseMs = 0;
      s.timerStartedAt = null;
    }),
});
